import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers, getCurrentBillingYear } from '../../../utils/helpers';
import { SortDirection, MemberSuffix, MemberTitle } from '../../../utils/constants/enums';
import {
  IPagedResult,
  IPaymentReportDto,
  IPaymentReportRequest,
  IPaymentSearchRequest,
  ISystemCodeByTypeDto
} from '../../../utils/constants/interfaces';
import { ReportColumn, SearchCriterion } from '../../../components/ui/ReportActions';
import { useReportFormatters } from '../../../utils/hooks/useReportFormatters';

const emptySearchResults: IPagedResult<IPaymentReportDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

const usePaymentReport = () => {
  const formatters = useReportFormatters();

  const defaultSearchParams: IPaymentSearchRequest = {
    // C# PaymentReportRequest properties
    clubMemberId: null,
    billingYear: getCurrentBillingYear(),
    paymentDateFrom: null,
    paymentDateThru: null,
    firstName: '',
    lastName: '',
    firstJoinedDateFrom: null,
    firstJoinedDateThru: null,
    orderNumber: '',
    transactionId: '',
    orderId: '',
    paymentMethodIds: [],
    // Pagination and sorting
    sortDirection: SortDirection.ASCENDING,
    sortField: 'id', // Default sort by Payment ID ascending
    pageSize: 50,
    page: 1
  };

  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [allResults, setAllResults] = useState<IPaymentReportDto[]>([]);
  const [paymentPagedResults, setPaymentPagedResults] =
    useState<IPagedResult<IPaymentReportDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] =
    useState<IPaymentSearchRequest>(defaultSearchParams);

  // Payment method options state
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<ISystemCodeByTypeDto[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState<boolean>(false);

  // Load payment method options on component mount
  useEffect(() => {
    const loadPaymentMethods = async () => {
      setLoadingPaymentMethods(true);
      try {
        const response = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/PAYMENT_TYPE'
        );
        if (response?.status?.statusCode === 200 && response.response) {
          setPaymentMethodOptions(response.response);
        }
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    void loadPaymentMethods();
  }, [api]);

  const hasSearchParametersCheck = useCallback(() => {
    return (
      searchParameters.clubMemberId !== null ||
      searchParameters.billingYear !== null ||
      !Helpers.isNullOrWhiteSpace(searchParameters.firstName) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.lastName) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.orderNumber) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.transactionId) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.orderId) ||
      searchParameters.paymentDateFrom !== null ||
      searchParameters.paymentDateThru !== null ||
      searchParameters.firstJoinedDateFrom !== null ||
      searchParameters.firstJoinedDateThru !== null ||
      searchParameters.paymentMethodIds.length > 0
    );
  }, [searchParameters]);

  // Function to apply client-side sorting and pagination
  const applyClientSidePaginationAndSort = useCallback(() => {
    if (allResults.length === 0) {
      setPaymentPagedResults(emptySearchResults);
      return;
    }

    // Apply sorting
    const sortedResults = [...allResults].sort((a, b) => {
      const field = searchParameters.sortField;
      const currentSortDirection: SortDirection = searchParameters.sortDirection;
      let aValue: string | number | Date = a[field as keyof IPaymentReportDto] as
        | string
        | number
        | Date;
      let bValue: string | number | Date = b[field as keyof IPaymentReportDto] as
        | string
        | number
        | Date;

      // Handle special object types
      if (field === 'paymentMethod') {
        const aPaymentMethod = a.paymentMethod as ISystemCodeByTypeDto | undefined;
        const bPaymentMethod = b.paymentMethod as ISystemCodeByTypeDto | undefined;
        aValue = aPaymentMethod?.description || aPaymentMethod?.codeValue || '';
        bValue = bPaymentMethod?.description || bPaymentMethod?.codeValue || '';
      }

      // Handle firstName and lastName fields directly
      if (field === 'firstName') {
        aValue = a.firstName || '';
        bValue = b.firstName || '';
      }

      if (field === 'lastName') {
        aValue = a.lastName || '';
        bValue = b.lastName || '';
      }

      // Handle calculated fields
      if (field === 'ccFees') {
        // CC Fees is always 0.00, so all values are equal
        aValue = 0;
        bValue = 0;
      }

      if (field === 'net') {
        // Net is Amount - CC Fees (CC Fees is always 0.00, so Net = Amount)
        aValue = a.amount;
        bValue = b.amount;
      }

      if (field === 'remainingBalance') {
        aValue = a.remainingBalance ?? 0;
        bValue = b.remainingBalance ?? 0;
      }

      // Handle dates with proper type checking
      if (field === 'paymentDateUtc' || field === 'firstJoinedDateUtc') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Convert to strings for comparison if needed
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null values for comparison
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      if (bValue === null) return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;

      if (aValue < bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });

    // Apply pagination
    const totalResults = sortedResults.length;
    const pageCount = Math.ceil(totalResults / searchParameters.pageSize);
    const startIndex = (searchParameters.page - 1) * searchParameters.pageSize;
    const endIndex = startIndex + searchParameters.pageSize;
    const pageResults = sortedResults.slice(startIndex, endIndex);

    const pagedResult: IPagedResult<IPaymentReportDto[]> = {
      currentPage: searchParameters.page,
      results: pageResults,
      pageSize: searchParameters.pageSize,
      pageCount: pageCount,
      resultCount: totalResults
    };

    setPaymentPagedResults(pagedResult);
  }, [
    allResults,
    searchParameters.sortField,
    searchParameters.sortDirection,
    searchParameters.page,
    searchParameters.pageSize
  ]);

  const search = useCallback(async (): Promise<void> => {
    // Only search if there are search parameters
    if (!hasSearchParametersCheck()) {
      setAllResults([]);
      setPaymentPagedResults(emptySearchResults);
      return;
    }

    setLoading(true);

    // Build the request body to match C# PaymentReportRequest exactly
    // Convert paymentMethodIds to full SystemCode objects
    const selectedPaymentMethods = searchParameters.paymentMethodIds
      .map((id: number) => paymentMethodOptions.find((opt) => opt.id === id))
      .filter((method): method is ISystemCodeByTypeDto => method !== undefined);

    const requestBody: IPaymentReportRequest = {
      clubMemberId: searchParameters.clubMemberId,
      billingYear: searchParameters.billingYear,
      paymentDateFrom: searchParameters.paymentDateFrom,
      paymentDateThru: searchParameters.paymentDateThru,
      firstName: searchParameters.firstName || '',
      lastName: searchParameters.lastName || '',
      firstJoinedDateFrom: searchParameters.firstJoinedDateFrom,
      firstJoinedDateThru: searchParameters.firstJoinedDateThru,
      orderNumber: searchParameters.orderNumber || '',
      transactionId: searchParameters.transactionId || '',
      orderId: searchParameters.orderId || '',
      paymentMethods: selectedPaymentMethods
    };

    const endpoint = '/members/payments';
    const apiResponse = await api.post<IPaymentReportDto[]>(endpoint, JSON.stringify(requestBody));

    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      // Store all results for client-side pagination
      setAllResults(apiResponse.response);
    } else {
      // Handle error
      setAllResults([]);
      setPaymentPagedResults(emptySearchResults);
    }
    setLoading(false);
  }, [
    api,
    hasSearchParametersCheck,
    searchParameters.clubMemberId,
    searchParameters.billingYear,
    searchParameters.paymentDateFrom,
    searchParameters.paymentDateThru,
    searchParameters.firstName,
    searchParameters.lastName,
    searchParameters.firstJoinedDateFrom,
    searchParameters.firstJoinedDateThru,
    searchParameters.orderNumber,
    searchParameters.transactionId,
    searchParameters.orderId,
    searchParameters.paymentMethodIds,
    paymentMethodOptions
  ]);

  // Apply pagination and sorting whenever allResults or pagination/sort parameters change
  useEffect(() => {
    applyClientSidePaginationAndSort();
  }, [applyClientSidePaginationAndSort]);

  const triggerSearch = () => {
    void search();
  };

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
    setAllResults([]);
    setPaymentPagedResults(emptySearchResults);
  };

  const changePage = (page: number) => {
    setSearchParameters({ ...searchParameters, page: page });
  };

  const changeSort = (sortField: string) => {
    const currentSortDirection: SortDirection = searchParameters.sortDirection;
    const sortDirection =
      searchParameters.sortField === sortField
        ? currentSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
        : SortDirection.ASCENDING;
    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection,
      page: 1 // Reset to page 1 when sorting
    });
  };

  const updateFilter = (field: string, value: string | number[]) => {
    const localSearchParams = { ...searchParameters };
    localSearchParams.page = 1; // Reset to page 1 on new search

    // Handle different field types
    if (field === 'clubMemberId') {
      localSearchParams.clubMemberId = value ? parseInt(value as string) : null;
    } else if (field === 'billingYear') {
      localSearchParams.billingYear = value ? parseInt(value as string) : null;
    } else if (field === 'firstName') {
      localSearchParams.firstName = value as string;
    } else if (field === 'lastName') {
      localSearchParams.lastName = value as string;
    } else if (field === 'orderNumber') {
      localSearchParams.orderNumber = value as string;
    } else if (field === 'transactionId') {
      localSearchParams.transactionId = value as string;
    } else if (field === 'orderId') {
      localSearchParams.orderId = value as string;
    } else if (field === 'paymentDateFrom') {
      localSearchParams.paymentDateFrom = value ? new Date(value as string) : null;
    } else if (field === 'paymentDateThru') {
      localSearchParams.paymentDateThru = value ? new Date(value as string) : null;
    } else if (field === 'firstJoinedDateFrom') {
      localSearchParams.firstJoinedDateFrom = value ? new Date(value as string) : null;
    } else if (field === 'firstJoinedDateThru') {
      localSearchParams.firstJoinedDateThru = value ? new Date(value as string) : null;
    } else if (field === 'paymentMethodIds') {
      localSearchParams.paymentMethodIds = value as number[];
    }

    setSearchParameters(localSearchParams);
  };

  // Define column configuration for PDF printing (minimal set)
  const getPrintColumns = (): ReportColumn<IPaymentReportDto>[] => [
    { key: 'id', header: 'PM_ID' },
    { key: 'clubMemberId', header: 'CM_ID' },
    {
      key: 'firstName',
      header: 'First Name'
    },
    {
      key: 'lastName',
      header: 'Last Name'
    },
    { key: 'billingYear', header: 'For Year' },
    {
      key: 'paymentDateUtc',
      header: 'Date',
      formatter: (value) => formatters.formatDateFromUtc(value as Date | string | null)
    },
    {
      key: 'paymentMethod',
      header: 'Pay Method',
      formatter: (value) => {
        const method = value as ISystemCodeByTypeDto;
        return method?.description || method?.codeValue || 'Unknown';
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'amount',
      header: 'Net',
      formatter: (value) => formatters.formatCurrency((value as number) - 0) // Amount - CC Fees
    },
    {
      key: 'remainingBalance',
      header: 'Balance',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    }
  ];

  // Define column configuration for CSV exports (full set)
  const getExportColumns = (): ReportColumn<IPaymentReportDto>[] => [
    { key: 'id', header: 'PM_ID' },
    { key: 'clubMemberId', header: 'CM_ID' },
    {
      key: 'lastName',
      header: 'Last Name'
    },
    {
      key: 'firstName',
      header: 'First Name'
    },
    {
      key: 'suffix',
      header: 'Suffix',
      formatter: (value) => {
        const suffixValue = value as MemberSuffix;
        return suffixValue === MemberSuffix.None ? '' : MemberSuffix[suffixValue];
      }
    },
    {
      key: 'title',
      header: 'Title',
      formatter: (value) => {
        const titleValue = value as MemberTitle;
        return titleValue === MemberTitle.None ? '' : MemberTitle[titleValue];
      }
    },
    { key: 'billingYear', header: 'For Year' },
    {
      key: 'paymentDateUtc',
      header: 'Payment Date',
      formatter: (value) => formatters.formatDateFromUtc(value as Date | string | null)
    },
    {
      key: 'paymentMethod',
      header: 'Payment Method',
      formatter: (value) => {
        const method = value as ISystemCodeByTypeDto;
        return method?.description || method?.codeValue || 'Unknown';
      }
    },
    {
      key: 'amount',
      header: 'Amount Paid',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'amount',
      header: 'Net Amount',
      formatter: (value) => formatters.formatCurrency((value as number) - 0) // Amount - CC Fees
    },
    {
      key: 'remainingBalance',
      header: 'Remaining Balance',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    { key: 'transactionId', header: 'Transaction ID' },
    { key: 'notes', header: 'Notes' },
    { key: 'createdBy', header: 'Created By' },
    {
      key: 'dateCreatedUtc',
      header: 'Created Date',
      formatter: (value) => formatters.formatDateFromUtc(value as Date | string | null)
    },
    { key: 'lastModifiedBy', header: 'Updated By' },
    {
      key: 'lastModifiedDateUtc',
      header: 'Updated Date',
      formatter: (value) => formatters.formatDateFromUtc(value as Date | string | null)
    }
  ];

  // Build search criteria for print display
  const getSearchCriteria = (): SearchCriterion[] => {
    const criteria: SearchCriterion[] = [];

    if (searchParameters.clubMemberId !== null) {
      criteria.push({ label: 'CM ID', value: searchParameters.clubMemberId.toString() });
    }

    if (searchParameters.billingYear !== null) {
      criteria.push({ label: 'Billing Year', value: searchParameters.billingYear.toString() });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.firstName)) {
      criteria.push({ label: 'First Name', value: searchParameters.firstName });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.lastName)) {
      criteria.push({ label: 'Last Name', value: searchParameters.lastName });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.orderNumber)) {
      criteria.push({ label: 'Order Number', value: searchParameters.orderNumber });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.transactionId)) {
      criteria.push({ label: 'Transaction ID', value: searchParameters.transactionId });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.orderId)) {
      criteria.push({ label: 'Order ID', value: searchParameters.orderId });
    }

    if (searchParameters.paymentDateFrom) {
      criteria.push({
        label: 'Payment Date From',
        value: formatters.formatDate(searchParameters.paymentDateFrom)
      });
    }

    if (searchParameters.paymentDateThru) {
      criteria.push({
        label: 'Payment Date Thru',
        value: formatters.formatDate(searchParameters.paymentDateThru)
      });
    }

    if (searchParameters.firstJoinedDateFrom) {
      criteria.push({
        label: 'First Joined Date From',
        value: formatters.formatDate(searchParameters.firstJoinedDateFrom)
      });
    }

    if (searchParameters.firstJoinedDateThru) {
      criteria.push({
        label: 'First Joined Date Thru',
        value: formatters.formatDate(searchParameters.firstJoinedDateThru)
      });
    }

    if (searchParameters.paymentMethodIds.length > 0) {
      const paymentMethodNames = searchParameters.paymentMethodIds
        .map((id: number) => {
          const method = paymentMethodOptions.find((opt) => opt.id === id);
          return method?.description || method?.codeValue || 'Unknown';
        })
        .join(', ');
      criteria.push({ label: 'Payment Methods', value: paymentMethodNames });
    }

    return criteria;
  };

  // Get payment method totals for the report
  const getPaymentMethodTotals = useCallback(() => {
    const totals: {
      [key: string]: { count: number; amount: number; memberships: Set<number> };
    } = {};

    allResults.forEach((payment) => {
      const methodName =
        payment.paymentMethod?.description || payment.paymentMethod?.codeValue || 'Unknown';
      if (!totals[methodName]) {
        totals[methodName] = { count: 0, amount: 0, memberships: new Set() };
      }
      totals[methodName].count++;
      totals[methodName].amount += payment.amount;
      totals[methodName].memberships.add(payment.clubMemberId);
    });

    return Object.entries(totals).map(([paymentMethod, data]) => ({
      paymentMethod,
      count: data.count,
      membershipCount: data.memberships.size,
      amount: data.amount
    }));
  }, [allResults]);

  const getReportFooterHtml = useCallback(() => {
    const totalRecords = allResults.length;
    const distinctMembers = new Set(allResults.map((p) => p.clubMemberId)).size;
    const totalAmount = allResults.reduce((sum, payment) => sum + payment.amount, 0);

    return `
      <h3>Report Totals:</h3>
      <table class="footer-totals-table">
        <thead>
          <tr>
            <th>Number of Payment Records</th>
            <th>Distinct Membership Count</th>
            <th>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${totalRecords}</td>
            <td>${distinctMembers}</td>
            <td>${formatters.formatCurrency(totalAmount)}</td>
          </tr>
        </tbody>
      </table>
    `;
  }, [allResults, formatters]);

  return {
    loading,
    searchParameters,
    paymentPagedResults,
    allResults, // Export the full dataset for CSV/Print
    updateFilter,
    hasSearchParameters: hasSearchParametersCheck,
    changePage,
    changeSort,
    setSearchParameters,
    resetSearch,
    triggerSearch,
    getPrintColumns,
    getExportColumns,
    getSearchCriteria, // Export search criteria for print
    paymentMethodOptions,
    loadingPaymentMethods,
    getPaymentMethodTotals,
    getReportFooterHtml
  };
};

export default usePaymentReport;
